import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { Observable, catchError, throwError } from 'rxjs';

// Guard flag to prevent multiple 401 responses from triggering
// simultaneous sign-out + redirect cycles (e.g. when forkJoin fires
// several requests and all return 401 at once).
let isHandling401 = false;

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const router = inject(Router);

    // Clone the request to include withCredentials for cookie-based auth.
    // No Authorization header needed — JWT is sent automatically via HttpOnly cookie.
    let newReq = req.clone({
        withCredentials: true,
    });

    // Response
    return next(newReq).pipe(
        catchError((error) => {
            // Catch "401 Unauthorized" responses
            if (error instanceof HttpErrorResponse && error.status === 401 && !isHandling401) {

                // Skip 401 handling for auth endpoints — these are EXPECTED to
                // return 401 when the user is not authenticated (e.g. /Auth/check
                // during guard verification, or /Auth/logout during sign-out).
                const isAuthEndpoint =
                    req.url.includes('/Auth/check') ||
                    req.url.includes('/Auth/logout') ||
                    req.url.includes('/Auth/login');

                if (!isAuthEndpoint) {
                    isHandling401 = true;

                    // Sign out then navigate to sign-in.
                    // Both success and error paths reset the flag and redirect
                    // so the app never gets stuck in a frozen state.
                    authService.signOut().subscribe({
                        next: () => {
                            isHandling401 = false;
                            router.navigateByUrl('/sign-in');
                        },
                        error: () => {
                            isHandling401 = false;
                            router.navigateByUrl('/sign-in');
                        },
                    });
                }
            }

            return throwError(() => error);
        })
    );
};
